import { action, makeObservable, observable } from 'mobx';

import Auth from './AuthManager';

const COURSE_RESOURCE_FILTER_ENDPOINTS = {
  FETCH_RESOURCE_CATEGORIES_AND_TAGS: '/api/viewResourceCategoriesAndTagsByParentForClassroom'
};

export class CourseResourceFilterManager {
  constructor() {
    makeObservable(this);
  }

  @observable allowCourseResourceFiltering = false; // satellite flag
  @observable allowDebugCourseResourceFiltering = false; // satellite flag (for debugging)

  @observable availableCourseResourceFilterCategories = [];
  @observable selectedCourseResourceFilterTagIds = [];

  /**
   * Set whether course resource filtering is enabled
   * @param {boolean} toggle - Enable or disable filtering
   */
  @action setAllowCourseResourceFiltering = (toggle) => {
    this.allowCourseResourceFiltering = toggle;
  }

  /**
   * Set whether course resource filtering debug output is enabled
   * @param {boolean} toggle - Enable or disable debug output
   */
  @action setAllowDebugCourseResourceFiltering = (toggle) => {
    this.allowDebugCourseResourceFiltering = toggle;
  }

  /**
   * Set available filter categories with nested tags
   * @param {Array} categories - Categories with nested tag arrays
   */
  @action setAvailableCourseResourceFilterCategories = (categories) => {
    this.availableCourseResourceFilterCategories = categories || [];
  }

  /**
   * Set selected tag IDs for filtering
   * @param {string[]} tagIds - Array of tag ID strings
   */
  @action setSelectedCourseResourceFilterTagIds = (tagIds) => {
    this.selectedCourseResourceFilterTagIds = tagIds || [];
  }

  @action clearAll = () => {
    this.selectedCourseResourceFilterTagIds = [];
    this.availableCourseResourceFilterCategories = [];
  }

  /**
   * Fetch available categories and tags for course resource filtering
   * @param {Object} params
   * @param {string} params.classroomId
   * @param {string} params.courseContentItemId
   * @param {string} params.courseResourceElementId
   * @param {boolean} params.includeAlignments
   * @returns {Promise<Array>} Categories with nested tags
   */
  fetchCourseResourceCategoriesAndTags = async ({
    classroomId,
    courseContentItemId,
    courseResourceElementId = null,
    includeAlignments = false
  } = {}) => {
    const endpoint = COURSE_RESOURCE_FILTER_ENDPOINTS.FETCH_RESOURCE_CATEGORIES_AND_TAGS;
    let apiUrl = `${Auth.ecms}${endpoint}?classroomId=${classroomId}&courseContentItemId=${courseContentItemId}`;
    apiUrl += `&includeAlignments=${includeAlignments}`;
    if (courseResourceElementId) {
      apiUrl += `&courseResourceElementId=${courseResourceElementId}`;
    }

    try {
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      return response?.categories || [];
    } catch (error) {
      console.error('Error fetching resource categories and tags:', error);
      return [];
    }
  }
}

export default new CourseResourceFilterManager();
